/**
 * Standardized date formatting utility for the platform.
 * Uses DD MMM YYYY format (e.g., "13 Mar 2026") for consistency.
 * All dates are displayed in the user's local timezone.
 */

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Parse an ISO string as UTC and return a Date object.
 *
 * MongoDB / FastAPI often emit naive-datetime ISO strings like
 * `"2026-07-13T22:22:00.000000"` without a `Z` suffix. Per the ECMAScript
 * spec, `new Date("2026-07-13T22:22:00")` treats the string as **local
 * time**, which caused IST users to see UTC times displayed unshifted
 * (e.g. "Jul 13, 10:22 PM" for what should have been "Jul 14, 03:52 AM IST").
 *
 * This helper appends `Z` when no timezone marker is present so the
 * resulting `Date` is anchored in UTC and downstream `.toLocaleString(...)`
 * calls render correctly in the browser's local timezone.
 */
export const parseIsoAsUtc = (dateStr) => {
  if (dateStr == null) return null;
  if (dateStr instanceof Date) return dateStr;
  if (typeof dateStr !== 'string') return new Date(dateStr);
  // Datetime-only ISO strings (contain `T`) without a timezone marker
  // must be pinned to UTC. Pure date strings ("2026-07-13") are left
  // alone — `Date()` treats those as UTC midnight already.
  const hasTz = dateStr.endsWith('Z')
    || /[+-]\d{2}:?\d{2}$/.test(dateStr)
    || dateStr.endsWith('+00:00');
  const needsZ = dateStr.includes('T') && !hasTz;
  const iso = needsZ ? dateStr + 'Z' : dateStr;
  return new Date(iso);
};

/**
 * Format a date as "DD MMM YYYY" (e.g., "13 Mar 2026") in user's local timezone
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    // Ensure UTC dates are properly parsed (append Z if no timezone info)
    const normalized = typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)
      ? dateStr + 'Z'
      : dateStr;
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = MONTHS_SHORT[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return '';
  }
};

/**
 * Format a date with time as "DD MMM YYYY, HH:MM" (e.g., "13 Mar 2026, 14:30")
 * Displayed in user's local timezone with timezone abbreviation
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    // Ensure UTC dates are properly parsed (append Z if no timezone info)
    const normalized = typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)
      ? dateStr + 'Z'
      : dateStr;
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = MONTHS_SHORT[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    // Get short timezone name (e.g., "IST", "EST", "PST")
    const tz = new Intl.DateTimeFormat('en', { timeZoneName: 'short' }).formatToParts(date).find(p => p.type === 'timeZoneName')?.value || '';
    return `${day} ${month} ${year}, ${hours}:${minutes} ${tz}`.trim();
  } catch {
    return '';
  }
};

/**
 * Format a date range (e.g., "13 Mar 2026 - 15 Jun 2026")
 */
export const formatDateRange = (startStr, endStr) => {
  const start = formatDate(startStr);
  if (!endStr) return `${start} - Ongoing`;
  const end = formatDate(endStr);
  return `${start} - ${end}`;
};
